import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Standalone HTML -> paginated A4 PDF exporter for company-standardized resumes.
 * Mirrors the capture/pagination technique already used on the Resume Formatter
 * page but is intentionally separate so the legacy flow stays untouched.
 */

const A4_WIDTH_PX = 794; // 210mm @ 96dpi
const A4_RATIO = 297 / 210;

export function splitFullHtmlDocument(htmlString) {
  const trimmed = String(htmlString || '').trim();
  try {
    if (/^<!DOCTYPE|^<html[\s>]/i.test(trimmed)) {
      const doc = new DOMParser().parseFromString(trimmed, 'text/html');
      const styles = Array.from(doc.querySelectorAll('style'))
        .map((el) => el.textContent || '')
        .join('\n');
      return { styles, bodyHtml: doc.body ? doc.body.innerHTML : trimmed };
    }
  } catch {
    // fall through
  }
  const styleMatches = trimmed.match(/<style[^>]*>([\s\S]*?)<\/style>/gi) || [];
  const styles = styleMatches
    .map((m) => m.replace(/<\/?style[^>]*>/gi, ''))
    .join('\n');
  const withoutStyles = trimmed.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  return { styles, bodyHtml: withoutStyles };
}

function rowIsBlank(ctx, y, width) {
  const step = Math.max(4, Math.floor(width / 160));
  try {
    const data = ctx.getImageData(0, y, width, 1).data;
    for (let x = 0; x < width * 4; x += step * 4) {
      const r = data[x];
      const g = data[x + 1];
      const b = data[x + 2];
      const a = data[x + 3];
      if (a > 12 && !(r > 245 && g > 245 && b > 245)) return false;
    }
  } catch {
    return false;
  }
  return true;
}

function findSmartBreak(ctx, start, idealEnd, hardMax, width) {
  const window = Math.min(220, Math.max(60, Math.floor((idealEnd - start) * 0.25)));
  let best = -1;
  const searchStart = Math.max(start + 40, idealEnd - window);
  for (let y = searchStart; y <= idealEnd; y += 2) {
    if (rowIsBlank(ctx, y, width)) {
      best = y;
    }
  }
  if (best > start + 20) return best;
  return Math.min(idealEnd, hardMax);
}

export async function downloadStandardizedPdf(fullHtml, filename) {
  const { styles, bodyHtml } = splitFullHtmlDocument(fullHtml);

  const holder = document.createElement('div');
  holder.style.position = 'fixed';
  holder.style.left = '-10000px';
  holder.style.top = '0';
  holder.style.background = '#ffffff';
  holder.style.width = `${A4_WIDTH_PX}px`;
  holder.style.zIndex = '-1';
  holder.innerHTML =
    `<style>${styles}
      .rc-page{margin:0 !important;box-shadow:none !important;}
      body{background:#fff !important;}
    </style><div id="rc-export-root">${bodyHtml}</div>`;
  document.body.appendChild(holder);

  try {
    const target = holder.querySelector('#rc-export-root') || holder;
    await new Promise((r) => setTimeout(r, 120));
    const canvas = await html2canvas(target, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
    });

    const pdfWidthPt = 595.28; // A4 width in pt
    const totalH = canvas.height;
    const pageH = Math.floor(canvas.width * A4_RATIO);
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
    const ctx = canvas.getContext('2d');

    let startY = 0;
    let pageIndex = 0;
    while (startY < totalH - 2 && pageIndex < 30) {
      const idealEnd = Math.min(startY + pageH, totalH);
      let end = idealEnd;
      if (idealEnd < totalH) {
        end = findSmartBreak(ctx, startY, idealEnd, totalH, canvas.width);
        if (totalH - end < 60) end = totalH; // absorb tiny remainder onto current page
      }
      const sliceH = end - startY;
      const outH = (sliceH / canvas.width) * pdfWidthPt;
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceH;
      const pctx = pageCanvas.getContext('2d');
      pctx.fillStyle = '#ffffff';
      pctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      pctx.drawImage(canvas, 0, startY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
      if (pageIndex > 0) pdf.addPage('a4', 'portrait');
      pdf.addImage(pageCanvas, 'JPEG', 0, 0, pdfWidthPt, outH, `p${pageIndex}`, 'FAST');
      startY = end;
      pageIndex += 1;
    }

    pdf.save(filename || 'standardized_resume.pdf');
    return true;
  } finally {
    document.body.removeChild(holder);
  }
}

export function base64ToBlob(base64, mime) {
  const byteChars = atob(String(base64 || ''));
  const byteArrays = [];
  const chunk = 1024;
  for (let offset = 0; offset < byteChars.length; offset += chunk) {
    const slice = byteChars.slice(offset, offset + chunk);
    const bytes = new Uint8Array(slice.length);
    for (let i = 0; i < slice.length; i += 1) bytes[i] = slice.charCodeAt(i);
    byteArrays.push(bytes);
  }
  return new Blob(byteArrays, { type: mime });
}

export function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(url), 4000);
}
